import { useLang } from "../../i18n/LanguageContext";
import { scrollToId } from "../../lib/smoothScroll";
import { Link } from "react-router-dom";
import { Download, Newspaper } from "lucide-react";

export const Footer = () => {
  const { t } = useLang();
  const year = new Date().getFullYear();
  return (
    <footer className="relative bg-ink bp-grid-dark border-t border-[#f8f7f4]/10" data-testid="footer">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <span className="chamfer-sm flex h-10 w-10 items-center justify-center bg-red-brand font-mono-tech text-xs font-bold text-[#f8f7f4]">MT</span>
              <span className="font-display font-black tracking-tight text-xl text-[#f8f7f4]">
                MARG<span className="text-[#e63946]">TOR</span>
              </span>
            </div>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-[#f8f7f4]/55">{t.footer.tagline}</p>
            <p className="mt-4 font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#f8f7f4]/35">
              {t.footer.legal}
            </p>
            <p className="mt-2 font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#f8f7f4]/35">
              {t.footer.groupPre}{" "}
              <a
                data-testid="footer-arthur-toren-link"
                href="https://arthurtoren.co.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#f8f7f4]/60 underline underline-offset-4 decoration-[#f8f7f4]/20 transition-colors duration-200 hover:text-[#e63946]"
              >
                {t.footer.groupLink}
              </a>
            </p>
            <p className="mt-3 max-w-sm text-xs leading-relaxed text-[#f8f7f4]/45" data-testid="footer-group-desc">
              {t.footer.groupDesc}{" "}
              <a href="https://arthurtoren.co.uk" target="_blank" rel="noopener noreferrer" data-testid="footer-group-site" className="font-mono-tech text-[10px] tracking-[0.15em] text-[#e63946] hover:text-[#f8f7f4] transition-colors">{t.footer.groupSite}</a>
            </p>
          </div>
          <div className="md:col-span-3">
            <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#e63946]">{t.footer.nav}</p>
            <ul className="mt-5 space-y-3">
              {["solutions", "manifesto", "markets", "micro", "financing", "economics", "founders", "contact"].map((id) => (
                <li key={id}>
                  <button
                    data-testid={`footer-link-${id}`}
                    onClick={() => scrollToId(id)}
                    className="text-sm text-[#f8f7f4]/60 hover:text-[#f8f7f4] transition-colors duration-200"
                  >
                    {t.nav[id]}
                  </button>
                </li>
              ))}
              <li>
                <Link
                  data-testid="footer-link-partners"
                  to="/partners"
                  className="text-sm text-[#f8f7f4]/60 hover:text-[#f8f7f4] transition-colors duration-200"
                >
                  {t.partners.eyebrow}
                </Link>
              </li>
              <li>
                <Link
                  data-testid="footer-link-knowledge"
                  to="/knowledge"
                  className="text-sm text-[#f8f7f4]/60 hover:text-[#f8f7f4] transition-colors duration-200"
                >
                  {t.knowledge.eyebrow}
                </Link>
              </li>
            </ul>
          </div>
          <div className="md:col-span-4">
            <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#e63946]">{t.footer.contact}</p>
            <p className="mt-5 text-sm text-[#f8f7f4]/60">{t.footer.offices}</p>
            <a
              data-testid="footer-email-link"
              href={`mailto:${t.contact.contactEmail}`}
              className="mt-4 block font-mono-tech text-[11px] tracking-[0.15em] text-[#f8f7f4]/80 transition-colors duration-200 hover:text-[#e63946]"
            >
              {t.contact.contactEmail}
            </a>
            <a
              data-testid="footer-website-link"
              href="https://www.margtor.africa"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block font-mono-tech text-[11px] tracking-[0.15em] text-[#e63946] transition-colors duration-200 hover:text-[#f8f7f4]"
            >
              www.margtor.africa
            </a>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { label: "arthurtoren.co.uk", url: "https://arthurtoren.co.uk" },
                { label: "amperi.eu", url: "https://amperi.eu" },
                { label: "electro.technology", url: "https://electro.technology" },
                { label: "hadronenergy.pl", url: "https://hadronenergy.pl" },
                { label: "ultre.city", url: "https://ultre.city" },
              ].map((p) => (
                <a
                  key={p.url}
                  data-testid={`footer-partner-${p.label.replace(/[^a-z]/g, "-")}`}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-[#f8f7f4]/15 px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.15em] text-[#f8f7f4]/45 transition-colors duration-200 hover:border-[#e63946] hover:text-[#f8f7f4]"
                >
                  {p.label}
                </a>
              ))}
            </div>
            <a
              data-testid="footer-brief-download"
              href={t.contact.briefFile}
              download
              className="chamfer-sm mt-5 inline-flex items-center gap-2 border border-[#f8f7f4]/20 px-4 py-2 font-mono-tech text-[10px] tracking-[0.15em] text-[#f8f7f4]/70 transition-colors duration-200 hover:border-[#e63946] hover:text-[#f8f7f4]"
            >
              <Download className="h-3.5 w-3.5" />
              {t.contact.brief}
            </a>
            <Link
              to="/press"
              data-testid="footer-press-link"
              className="chamfer-sm mt-3 sm:ml-2 inline-flex items-center gap-2 border border-[#f8f7f4]/20 px-4 py-2 font-mono-tech text-[10px] tracking-[0.15em] text-[#f8f7f4]/70 transition-colors duration-200 hover:border-[#e63946] hover:text-[#f8f7f4]"
            >
              <Newspaper className="h-3.5 w-3.5" />
              {t.footer.press}
            </Link>
          </div>
        </div>
        <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-[#f8f7f4]/10 pt-7">
          <p className="font-mono-tech text-[10px] tracking-[0.2em] text-[#f8f7f4]/35">
            © {year} MARGTOR LTD. — {t.footer.rights}
          </p>
          <p className="font-mono-tech text-[10px] tracking-[0.2em] text-[#f8f7f4]/35">
            {t.footer.iso || "ISO-aligned safety & quality assurance"}
          </p>
          <p className="mt-2 font-mono-tech text-[9px] tracking-[0.15em] text-[#f8f7f4]/45">
            {t.footer.credits || "Street photography: Wikimedia Commons contributors (CC BY-SA) · Text & data:"}{" "}
            <a href="https://creativecommons.org/licenses/by/4.0/" rel="license noopener noreferrer" target="_blank" data-testid="footer-license" className="text-[#f8f7f4]/70 underline underline-offset-2 hover:text-[#f8f7f4]">CC BY 4.0</a>
            {" · "}
            <a href="/llms.txt" data-testid="footer-llms" className="text-[#f8f7f4]/70 underline underline-offset-2 hover:text-[#f8f7f4]">LLMS.TXT</a>
            {" · "}
            <a href="/api/feed.xml" data-testid="footer-feed" className="text-[#f8f7f4]/70 underline underline-offset-2 hover:text-[#f8f7f4]">ATOM</a>
          </p>
        </div>
      </div>
    </footer>
  );
};
